import React, { useContext, useEffect, useRef, useState } from 'react';
import { ROUTE_POINTS_START, ROUTE_POINTS_FINISH } from '../store/geoRouter/profileConstants';
import {
    Box,
    ClickAwayListener,
    Divider,
    Drawer,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    SvgIcon,
    Toolbar,
} from '@mui/material';
import { Menu } from '@mui/icons-material';
import LoginContext from '../context/LoginContext';
import MapContext from '../context/MapContext';
import WeatherContext from '../context/WeatherContext';
import AppContext, {
    OBJECT_CONFIGURE_MAP,
    OBJECT_GLOBAL_SETTINGS,
    OBJECT_SEARCH,
    OBJECT_TYPE_TRAVEL,
    OBJECT_TYPE_CLOUD_TRACK,
    OBJECT_TYPE_FAVORITE,
    OBJECT_TYPE_LOCAL_TRACK,
    OBJECT_TYPE_NAVIGATION_TRACK,
    OBJECT_TYPE_WEATHER,
    OBJECT_TRACK_ANALYZER,
    OBJECT_TYPE_NAVIGATION_ALONE,
    FAVORITES_URL_PARAM_FOLDER,
    TRAVEL_ROUTE_ID_PARAM,
} from '../context/AppContext';
import TravelContext from '../context/TravelContext';
import TracksMenu from './tracks/TracksMenu';
import VisibleTracks from './visibletracks/VisibleTracks';
import Empty from './errors/Empty';
import ConfigureMap from './configuremap/ConfigureMap';
import NavigationMenu from './navigation/NavigationMenu';
import { matchPath, useLocation, useNavigate, useOutlet, useParams, useSearchParams } from 'react-router-dom';
import FavoritesMenu from './favorite/FavoritesMenu';
import PlanRouteMenu, { openSelectedLocalTrack } from './planroute/PlanRouteMenu';
import { ReactComponent as FavoritesIcon } from '../assets/menu/ic_action_favorite.svg';
import { ReactComponent as WeatherIcon } from '../assets/menu/ic_action_umbrella.svg';
import { ReactComponent as TracksIcon } from '../assets/menu/ic_action_track.svg';
import { ReactComponent as NavigationIcon } from '../assets/menu/ic_action_navigation.svg';
import { ReactComponent as PlanRouteIcon } from '../assets/menu/ic_action_plan_route.svg';
import { ReactComponent as ConfigureMapIcon } from '../assets/icons/ic_map_configure_map.svg';
import { ReactComponent as SettingsIcon } from '../assets/icons/ic_action_settings_outlined.svg';
import { ReactComponent as TrackAnalyzerIcon } from '../assets/icons/ic_action_tool.svg';
import { ReactComponent as TravelIcon } from '../assets/icons/ic_action_activity.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/ic_action_search_dark.svg';
import InformationBlock from '../infoblock/components/InformationBlock';
import Weather, { FORECAST_SOURCE_PARAM, FORECAST_TYPE_PARAM, selectedForecastDetails } from './weather/Weather';
import styles from './mainmenu.module.css';
import isEmpty from 'lodash-es/isEmpty';
import { useTranslation } from 'react-i18next';
import SettingsMenu from './settings/SettingsMenu';
import CloudSettings from './settings/CloudSettings';
import { closeCloudSettings } from '../manager/SettingsManager';
import {
    CONFIGURE_URL,
    SEARCH_URL,
    FAVORITES_URL,
    INSTALL_BANNER_SIZE,
    MAIN_PAGE_TYPE,
    MAIN_URL_WITH_SLASH,
    MENU_INFO_CLOSE_SIZE,
    MENU_INFO_OPEN_SIZE,
    NAVIGATE_URL,
    PLANROUTE_URL,
    SETTINGS_URL,
    TRACKS_URL,
    VISIBLE_TRACKS_URL,
    WEATHER_URL,
    TRAVEL_URL,
    isTravelPath,
    SHARE_FILE_MAIN_URL,
    TRACK_ANALYZER_URL,
    INFO_MENU_URL,
    SHARE_MENU_URL,
    LOGIN_URL,
    DELETE_ACCOUNT_URL,
    WEATHER_FORECAST_URL,
    POI_CATEGORIES_URL,
    EXPLORE_URL,
    POI_URL,
    MENU_IDS,
    liveHash,
} from '../manager/GlobalManager';
import { LEFT_MENU_Z_INDEX, OPEN_LEFT_MENU_Z_INDEX, OPEN_MENU_INFOBLOCK_Z_INDEX } from '../map/util/ZIndexes';
import { createUrlParams, decodeString } from '../util/Utils';
import { useWindowSize } from '../util/hooks/useWindowSize';
import SearchMenu from './search/SearchMenu';
import LoginButton from '../login/LoginButton';
import TravelMenu from './travel/TravelMenu';
import ProFeatures from '../frame/pro/ProFeatures';
import { getShareFileInfo, updateUserRequests } from '../manager/ShareManager';
import { debouncer } from '../context/TracksRoutingCache';
import TrackAnalyzerMenu from './analyzer/TrackAnalyzerMenu';
import { processDisplayTrack, resetCloudTracksMenu } from '../manager/track/TracksManager';
import { getLoginRedirect, openLoginMenu } from '../manager/LoginManager';
import { saveSortToDB } from '../context/FavoriteStorage';
import {
    getFavMenuListByLayers,
    openFavoriteObj,
    addFavoriteToMap,
    resetFavoritesMenu,
} from '../manager/FavoritesManager';
import useMenuDots from '../util/hooks/menu/useMenuDots';
import {
    openPoiObj,
    openSearchObj,
    navigateBackToSearchResults,
    isFavoriteFromSearch,
    isTrackFromSearch,
    resetSearchMenu,
} from '../manager/SearchManager';
import { useRecentDataSaver } from '../util/hooks/menu/useRecentDataSaver';
import { useGeoLocation } from '../util/hooks/useGeoLocation';
import useSearchNav from '../util/hooks/search/useSearchNav';
import { navigateToPoi } from '../manager/PoiManager';
import { CATEGORY_TYPE } from '../infoblock/components/wpt/WptTagsProvider';
import { searchTypeMap } from '../manager/searchConstants';
import { useFocusMode } from '../util/hooks/map/useFocusMode';
import { MAP_VIEW_ZOOM_FIT } from '../map/util/MapManager';
import useCloseMenuByEsc from '../util/hooks/menu/useCloseMenuByEsc';

export function closeSubPages({ ctx, ltx, wptDetails = true, closeLogin = true }) {
    ctx.setOpenProFeatures(null);
    ctx.setSelectedTransportRoute(null);
    if (wptDetails) {
        ctx.setSelectedWpt(null);
    }
    ctx.setLoadingContextMenu(false);
    if (closeLogin) {
        ltx.setOpenLoginMenu(false);
    }
    ctx.setSelectedPhotoInd(-1);

    if (ctx.selectedGpxFile?.mapObj) {
        ctx.setSelectedGpxFile({});
    }
}

export default function MainMenu({
    size,
    infoSize,
    openMainMenu,
    setOpenMainMenu,
    menuInfo,
    setMenuInfo,
    showInfoBlock,
    setShowInfoBlock,
    setClearState,
    showInstallBanner,
}) {
    const ctx = useContext(AppContext);
    const ttx = useContext(TravelContext);
    const wtx = useContext(WeatherContext);
    const mtx = useContext(MapContext);
    const ltx = useContext(LoginContext);

    const { t } = useTranslation();
    const location = useLocation();
    const currentLoc = useGeoLocation(ctx);
    const { isSearchResultRoute } = useSearchNav();
    const { clearSelectionFocus } = useFocusMode();
    useCloseMenuByEsc();

    const outlet = useOutlet();
    const showDeleteOutlet = matchPath({ path: MAIN_URL_WITH_SLASH + DELETE_ACCOUNT_URL + '*' }, location.pathname);
    const showShareOutlet = matchPath({ path: MAIN_URL_WITH_SLASH + SHARE_FILE_MAIN_URL + '*' }, location.pathname);

    const [, height] = useWindowSize();

    const [searchParams] = useSearchParams();

    const { filename, favgroup, favname } = useParams();

    const isAccountOpen = location.pathname.startsWith(MAIN_URL_WITH_SLASH + LOGIN_URL) && ltx.openLoginMenu;
    const openObjectByUrl = location.pathname.includes(INFO_MENU_URL) && isEmpty(ctx.selectedGpxFile);

    const timerRef = useRef(null);
    const lastMenuUrlsRef = useRef({});

    const [selectedType, setSelectedType] = useState(null);
    const [openCloudSettings, setOpenCloudSettings] = useState(false);

    const [redirectUrl, setRedirectUrl] = useState(null);

    const [savePrevState, setSavePrevState] = useState(false);
    const [trackUrlOpenLoading, setTrackUrlOpenLoading] = useState(false);

    const menuDots = useMenuDots(ctx);
    const recentSaver = useRecentDataSaver();
    const openVisibleTracks = location.pathname === MAIN_URL_WITH_SLASH + VISIBLE_TRACKS_URL;
    const aloneVisibleTracks = openVisibleTracks && !ctx.openVisibleMenu.showTracks && !ctx.openVisibleMenu.showConfig;

    const navigate = useNavigate();

    const openMenuInNewTab = (item) => {
        if (!item) {
            return;
        }

        const origin = globalThis?.location?.origin ?? '';
        const targetUrl = new URL(item.url + liveHash(), origin).toString();
        globalThis.open(targetUrl, '_blank', 'noopener,noreferrer');
    };

    const handleMenuAuxClick = (event, item) => {
        if (event.button !== 1) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();

        openMenuInNewTab(item);
    };

    const handleMenuClick = (event, item) => {
        if (event.metaKey || event.ctrlKey) {
            event.preventDefault();
            event.stopPropagation();
            openMenuInNewTab(item);
            return;
        }
        selectMenu({ item });
    };

    const handleDrawer = () => {
        setOpenMainMenu(!openMainMenu);
    };

    useEffect(() => {
        if (location.pathname.startsWith(MAIN_URL_WITH_SLASH + LOGIN_URL)) {
            // keep the redirect even while the login state is still INIT; same-site paths only
            const to = getLoginRedirect(location.search);
            if (to) {
                setRedirectUrl(to);
            }
            if (!ltx.openLoginMenu && !ltx.loginUser) {
                setMenuInfo(null);
                openLoginMenu({ ctx, ltx, navigate, location });
            }
        }
    }, [location.pathname]);

    useEffect(() => {
        if (ltx.isLoggedIn() && redirectUrl) {
            globalThis.location.href = redirectUrl;
        }
    }, [ltx.loginUser, redirectUrl]);

    // open trackInfo/trackShareMenu after reload or open by link
    useEffect(() => {
        // Wait until .info details are loaded (updateFiles == null) before auto-opening the
        // track, so processDisplayTrack reads pointsGroups visibility from the refreshed listFiles.
        if (
            location.pathname.includes(INFO_MENU_URL) &&
            ctx.listFiles?.uniqueFiles &&
            !ctx.updateFiles &&
            !trackUrlOpenLoading
        ) {
            if (isEmpty(ctx.selectedGpxFile)) {
                if (filename) {
                    const decodeFilename = decodeString(filename);
                    const file = ctx.listFiles.uniqueFiles.find((file) => file.name === decodeFilename);
                    if (!file) {
                        return;
                    }
                    ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
                    const fileWithMapObj = { ...file, mapObj: true };
                    if (location.pathname.includes(SHARE_MENU_URL)) {
                        // open share menu
                        if (!ctx.shareFile) {
                            getShareFileInfo({ file: fileWithMapObj, ctx }).then();
                        }
                    } else {
                        // open track info
                        processDisplayTrack({
                            visible: true,
                            showOnMap: true,
                            showInfo: true,
                            zoomToTrack: true,
                            file: fileWithMapObj,
                            ctx,
                            fileStorage: ctx.gpxFiles,
                            setFileStorage: ctx.setGpxFiles,
                            recentSaver,
                            setLoading: setTrackUrlOpenLoading,
                        }).then();
                    }
                } else if (favgroup && favname && ctx.favorites?.groups) {
                    const group = ctx.favorites.groups.find((g) => g.name === decodeString(favgroup));
                    if (!group) return;

                    const mapObj = ctx.favorites.mapObjs[group.id];
                    if (!mapObj?.markers?._layers) return;

                    const markerList = getFavMenuListByLayers({
                        layers: mapObj.markers._layers,
                        wpts: mapObj.wpts,
                        currentLoc,
                        pointsGroups: mapObj.pointsGroups,
                    });
                    if (markerList.length === 0) return;

                    const marker = markerList.find((m) => m.name === decodeString(favname));

                    if (!marker) return;

                    ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
                    if (
                        ctx.selectedFavoriteObj?.name === marker.name &&
                        ctx.selectedFavoriteObj?.nameGroup === group.name
                    ) {
                        openFavoriteObj({ ctx, favoriteObj: ctx.selectedFavoriteObj });
                        return;
                    }
                    addFavoriteToMap({
                        group,
                        marker,
                        ctx,
                        sharedFile: location.pathname.includes(SHARE_MENU_URL),
                        mapObj: true,
                        openedFolder: searchParams.get(FAVORITES_URL_PARAM_FOLDER) ?? undefined,
                    });
                }
            }
        }
    }, [location.pathname, ctx.listFiles.uniqueFiles, ctx.favorites?.groups, ctx.updateFiles]);

    useEffect(() => {
        if (location.pathname.includes(POI_URL)) {
            return;
        }
        if (location.pathname.includes(INFO_MENU_URL)) {
            // Close login and switch menu if navigating to a track/favorites info URL from Login/Garmin
            if (ltx.openLoginMenu) {
                ltx.setOpenLoginMenu(false);
            }
            switchMenuByCurrentUrl();
            return;
        }
        if (savePrevState) {
            setSavePrevState(false);
            return;
        }
        if (location.pathname.includes(LOGIN_URL)) {
            ltx.setOpenLoginMenu(true);
            setShowInfoBlock(false);
            return;
        }
        if (openVisibleTracks) {
            ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
            return;
        }
        if (!menuInfo) {
            selectMenuByUrl();
        } else {
            // When navigating to a menu URL from a different menu,
            // switch directly without selectMenu's side-effects (closing infoBlock, clearing currentObjectType).
            switchMenuByCurrentUrl();
        }
        if (location.pathname === MAIN_URL_WITH_SLASH) {
            ctx.setInfoBlockWidth(`${MENU_INFO_CLOSE_SIZE}px`);
        }

        closeSubPages({ ctx, ltx, wptDetails: false });

        openShareFileByLink();

        const startCreateTrack = ctx.createTrack?.enable && location.pathname === MAIN_URL_WITH_SLASH + PLANROUTE_URL;
        const openCloudTrackAfterSave =
            ctx.selectedGpxFile.url && location.pathname === MAIN_URL_WITH_SLASH + TRACKS_URL;
        const openFavorite =
            !!ctx.selectedGpxFile?.markerCurrent && location.pathname === MAIN_URL_WITH_SLASH + FAVORITES_URL;
        // Don't close infoBlock if selectedWpt exists - it means user selected an object on map
        // (even if data is still loading asynchronously)
        const hasSelectedWpt = !!ctx.selectedWpt;
        if (
            !startCreateTrack &&
            !openCloudTrackAfterSave &&
            !openFavorite &&
            !ctx.selectedPoiObj &&
            !ctx.selectedSearchObj &&
            !hasSelectedWpt
        ) {
            setShowInfoBlock(false);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (ctx.selectedSort && (ctx.selectedSort.favorites || ctx.selectedSort?.tracks)) {
            saveSortToDB(ctx.selectedSort).then();
        }
    }, [ctx.selectedSort]);

    function selectMenuByUrl() {
        const item = items.find((item) => location.pathname.startsWith(item.url));
        if (item) {
            ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
            return selectMenu({ item, openFromUrl: true });
        }
        return null;
    }

    // Switch menu by current URL without selectMenu's side-effects (closing infoBlock, clearing currentObjectType).
    // Skip if a map object (e.g. favorite opened from map) is displayed — keep selectedType pointing at the
    // originating menu (e.g. cloud track) so that closeMapObj can navigate back to it correctly.
    function switchMenuByCurrentUrl() {
        if (ctx.selectedWpt?.mapObj || ctx.selectedWpt?.poi?.mapObj) {
            return;
        }
        if (ctx.selectedSearchObj) {
            return;
        }
        const matchedItem = items.find((item) => location.pathname.startsWith(item.url));
        if (matchedItem && !isSelectedMenuItem(matchedItem)) {
            setMenuInfo(matchedItem.component);
            setSelectedType(matchedItem.type);
        }
    }

    function openShareFileByLink() {
        const openShareFile = location.pathname.includes(SHARE_FILE_MAIN_URL);
        if (openShareFile) {
            setMenuInfo(null);
            setShowInfoBlock(true);
            ctx.setInfoBlockWidth(MENU_INFO_OPEN_SIZE + 'px');
        }
    }

    const handleClickAway = () => {
        setOpenMainMenu(false);
    };

    const items = [
        {
            name: t('shared_string_search'),
            icon: SearchIcon,
            component: <SearchMenu />,
            type: OBJECT_SEARCH,
            show: true,
            id: MENU_IDS.search,
            url: MAIN_URL_WITH_SLASH + SEARCH_URL,
            otherUrls: [MAIN_URL_WITH_SLASH + POI_URL],
            clearState: resetSearchMenu,
        },
        {
            name: t('configure_map'),
            icon: ConfigureMapIcon,
            component: <ConfigureMap />,
            type: OBJECT_CONFIGURE_MAP,
            show: true,
            id: MENU_IDS.config,
            url: MAIN_URL_WITH_SLASH + CONFIGURE_URL,
        },
        {
            name: t('shared_string_weather'),
            icon: WeatherIcon,
            component: <Weather />,
            type: OBJECT_TYPE_WEATHER,
            show: true,
            id: MENU_IDS.weather,
            url: MAIN_URL_WITH_SLASH + WEATHER_URL,
        },
        {
            name: t('shared_string_tracks'),
            icon: TracksIcon,
            component: <TracksMenu />,
            type: OBJECT_TYPE_CLOUD_TRACK,
            show: true,
            id: MENU_IDS.tracks,
            url: MAIN_URL_WITH_SLASH + TRACKS_URL,
            clearState: resetCloudTracksMenu,
        },
        {
            name: t('shared_string_my_favorites'),
            icon: FavoritesIcon,
            component: <FavoritesMenu />,
            type: OBJECT_TYPE_FAVORITE,
            show: true,
            id: MENU_IDS.favorites,
            url: MAIN_URL_WITH_SLASH + FAVORITES_URL,
            clearState: resetFavoritesMenu,
        },
        {
            name: t('shared_string_navigation'),
            icon: NavigationIcon,
            component: <NavigationMenu />,
            type: OBJECT_TYPE_NAVIGATION_TRACK, // shared with OBJECT_TYPE_NAVIGATION_ALONE
            show: true,
            id: MENU_IDS.navigation,
            url: MAIN_URL_WITH_SLASH + NAVIGATE_URL,
        },
        {
            name: t('plan_route'),
            icon: PlanRouteIcon,
            component: <PlanRouteMenu />,
            type: OBJECT_TYPE_LOCAL_TRACK,
            show: true,
            id: MENU_IDS.planroute,
            url: MAIN_URL_WITH_SLASH + PLANROUTE_URL,
        },
        {
            name: t('web:shared_string_travel'),
            icon: TravelIcon,
            component: <TravelMenu />,
            type: OBJECT_TYPE_TRAVEL,
            show: ctx.develFeatures,
            id: MENU_IDS.travel,
            url: MAIN_URL_WITH_SLASH + TRAVEL_URL,
        },
        {
            name: t('shared_string_settings'),
            icon: SettingsIcon,
            component: <SettingsMenu />,
            type: OBJECT_GLOBAL_SETTINGS,
            show: true,
            id: MENU_IDS.settings,
            url: MAIN_URL_WITH_SLASH + SETTINGS_URL,
        },
        {
            name: t('web:tracks_analyzer'),
            icon: TrackAnalyzerIcon,
            component: <TrackAnalyzerMenu />,
            type: OBJECT_TRACK_ANALYZER,
            show: true,
            id: MENU_IDS.trackanalyzer,
            url: MAIN_URL_WITH_SLASH + TRACK_ANALYZER_URL,
        },
    ];

    // Close is offered only by the menus that know how to forget their state
    const closableMenu = !!items.find(isSelectedMenuItem)?.clearState;

    useEffect(() => {
        ctx.setClosableMenu(closableMenu);
    }, [closableMenu]);

    useEffect(() => {
        setOpenCloudSettings(ctx.cloudSettings.changes || ctx.cloudSettings.trash);
    }, [ctx.cloudSettings]);

    // url caching for every menu type
    useEffect(() => {
        const menuByUrl = items.find(
            (i) => location.pathname.startsWith(i.url) || i.otherUrls?.some((u) => location.pathname.startsWith(u))
        );
        if (!menuByUrl) {
            return;
        }
        const mapObject = ctx.selectedWpt?.mapObj || ctx.selectedWpt?.poi?.mapObj;
        // a favorite clicked on the map drops the one opened from the list, so its folder is the menu behind
        if (mapObject && menuByUrl.type !== OBJECT_TYPE_FAVORITE) {
            return;
        }
        const menuUrl = mapObject || openObjectByUrl ? menuByUrl.url : location.pathname;
        lastMenuUrlsRef.current[menuByUrl.type] = menuUrl + location.search + liveHash();
    }, [location.pathname, location.search, location.hash]);

    // open menu after closing map object if any selected before
    useEffect(() => {
        if (ctx.closeMapObj) {
            if (!selectedType) {
                ctx.setInfoBlockWidth(`${MENU_INFO_CLOSE_SIZE}px`);
            } else {
                const saved = lastMenuUrlsRef.current[selectedType];
                if (saved) {
                    navigate(saved);
                    openMenuObject();
                }
            }
            ctx.setCloseMapObj(false);
        }
    }, [ctx.closeMapObj]);

    useEffect(() => {
        if (!ctx.closeSelectedMenu) return;
        ctx.setCloseSelectedMenu(false);
        const item = items.find(isSelectedMenuItem);
        if (!item) return;
        item.clearState?.(ctx);
        // the list item hovered before opening the object gets no mouseleave, drop its hover as Back does
        ctx.setSelectedWptId((prev) => (prev ? { ...prev, show: false } : prev));
        doSelectMenu({ item });
        // don't move the map back to the previous location
        mtx.setMapViewStack((prev) => prev.filter((entry) => entry.key !== MAP_VIEW_ZOOM_FIT));
        clearSelectionFocus();
        navigateToUrl({ isMain: true, params: ctx.pageParams });
    }, [ctx.closeSelectedMenu]);

    useEffect(() => {
        ttx.setOpenTravel(ctx.develFeatures && selectedType === OBJECT_TYPE_TRAVEL && ltx.isLoggedIn());
    }, [ctx.develFeatures, selectedType, ltx.loginUser]);

    useEffect(() => {
        openMenuObject();

        if (openVisibleTracksPage()) {
            return;
        }

        if (selectedType === OBJECT_TRACK_ANALYZER) {
            ctx.setCurrentObjectType(OBJECT_TRACK_ANALYZER);
        }

        if (selectedType === OBJECT_TYPE_WEATHER) {
            const res = selectedForecastDetails(ctx);
            if (res) {
                const type = res.key;
                navigate({
                    pathname: MAIN_URL_WITH_SLASH + WEATHER_URL + WEATHER_FORECAST_URL,
                    search: `?${FORECAST_TYPE_PARAM}=${type}&${FORECAST_SOURCE_PARAM}=${wtx.weatherType}`,
                    hash: liveHash(),
                });
                return;
            }
        }

        if (selectedType === OBJECT_TYPE_TRAVEL) {
            const savedTravelUrl = lastMenuUrlsRef.current[OBJECT_TYPE_TRAVEL];
            if (savedTravelUrl?.includes(TRAVEL_ROUTE_ID_PARAM)) {
                navigate(savedTravelUrl);
                return;
            }
        }

        ctx.setSearchSettings((prev) => ({
            ...prev,
            showExploreMarkers: selectedType === OBJECT_SEARCH ? !ctx.searchResult && !isSearchResultRoute : false,
        }));

        if (selectedType === OBJECT_SEARCH) {
            if (ctx.selectedPoiObj) {
                if (ctx.selectedPoiObj.wikidata) {
                    navigateToPoi(ctx.selectedPoiObj, navigate, true);
                    return;
                }
                if (ctx.selectedPoiObj.options?.[CATEGORY_TYPE] === searchTypeMap.POI) {
                    navigateToPoi({ poi: ctx.selectedPoiObj }, navigate);
                    return;
                }
            }
            if (ctx.poiCatMenu) {
                navigate(MAIN_URL_WITH_SLASH + SEARCH_URL + POI_CATEGORIES_URL + liveHash());
                return;
            }
            if (ctx.searchQuery) {
                navigateBackToSearchResults(navigate, ctx, location);
                return;
            }

            if (ctx.exploreMenu) {
                navigate(MAIN_URL_WITH_SLASH + SEARCH_URL + EXPLORE_URL + liveHash());
                return;
            }
        }

        if (selectedType === OBJECT_TYPE_FAVORITE && searchParams.get(FAVORITES_URL_PARAM_FOLDER)) {
            // open selected favorite folder only after loading favorites
            return;
        }

        const menu = items.find((item) => isSelectedMenuItem(item));
        menu && navigateToUrl({ menu, params: ctx.pageParams });
    }, [selectedType]);

    function openVisibleTracksPage() {
        const openFromConfig = selectedType === OBJECT_CONFIGURE_MAP && ctx.openVisibleMenu.showConfig;
        const openFromTracks = selectedType === OBJECT_TYPE_CLOUD_TRACK && ctx.openVisibleMenu.showTracks;
        if (openFromConfig || openFromTracks) {
            navigate(MAIN_URL_WITH_SLASH + VISIBLE_TRACKS_URL + globalThis.location.hash);
            return true;
        }
        return false;
    }

    useEffect(() => {
        const updateRequests = () => {
            updateUserRequests(ctx).then();
        };

        debouncer(updateRequests, timerRef, 3000);

        return () => clearTimeout(timerRef.current);
    }, [ctx.updatedRequestList]);

    // Select menu by object type. Only for actions from map context menu.
    useEffect(() => {
        if (ctx.currentObjectType) {
            closeCloudSettings(openCloudSettings, setOpenCloudSettings, ctx);
            if (ctx.currentObjectType === OBJECT_TYPE_NAVIGATION_ALONE) {
                // invoked by RouteService.js effect
                // activate Navigation menu even w/o currentObjectType (if no other menu was activated before)
                // use timeout to avoid GlobalFrame setMenuInfo(null) and to catch routeObject options start/end
                ctx.setCurrentObjectType(null); // get ready for next navigation changes
                setTimeout(() => {
                    if (
                        ctx.routeObject.getOption(ROUTE_POINTS_START) ||
                        ctx.routeObject.getOption(ROUTE_POINTS_FINISH)
                    ) {
                        selectMenuInfoByObjectType(OBJECT_TYPE_NAVIGATION_TRACK);
                    }
                }, 100);
            } else if (ctx.currentObjectType === OBJECT_TYPE_LOCAL_TRACK && ctx.createTrack?.enable) {
                selectMenuInfoByObjectType(OBJECT_TYPE_LOCAL_TRACK);
            }
        }
    }, [ctx.currentObjectType]);

    // Open info block if it was closed
    useEffect(() => {
        if (ctx.currentObjectType && ctx.infoBlockWidth === `${MENU_INFO_CLOSE_SIZE}px`) {
            ctx.setInfoBlockWidth(`${MENU_INFO_OPEN_SIZE}px`);
        }
    }, [ctx.currentObjectType]);

    // Close navigation settings if the navigation is closed
    useEffect(() => {
        if (
            selectedType !== OBJECT_TYPE_NAVIGATION_TRACK &&
            ctx.currentObjectType !== OBJECT_TYPE_NAVIGATION_TRACK &&
            ctx.currentObjectType !== OBJECT_TYPE_NAVIGATION_ALONE
        ) {
            ctx.setOpenNavigationSettings(false);
        }
    }, [ctx.currentObjectType, selectedType]);

    useEffect(() => {
        if (selectedType !== OBJECT_TYPE_TRAVEL && ctx.currentObjectType !== OBJECT_TYPE_TRAVEL) {
            ttx.setOpenTravelFilters(false);
        }
    }, [ctx.currentObjectType, selectedType]);

    useEffect(() => {
        if (ctx.saveTrackToCloud) {
            ctx.setCurrentObjectType(OBJECT_TYPE_CLOUD_TRACK);
            selectMenuInfoByObjectType(OBJECT_TYPE_CLOUD_TRACK);
            ctx.setSaveTrackToCloud(false);
        }
    }, [ctx.saveTrackToCloud]);

    function openMenuObject() {
        if (selectedType === OBJECT_TYPE_FAVORITE) {
            if (ctx.selectedFavoriteObj) {
                openFavoriteObj({ ctx, favoriteObj: ctx.selectedFavoriteObj });
            }
        }

        if (selectedType === OBJECT_TYPE_CLOUD_TRACK) {
            if (ctx.selectedCloudTrackObj) {
                processDisplayTrack({
                    visible: true,
                    showOnMap: true,
                    showInfo: true,
                    zoomToTrack: true,
                    file: ctx.selectedCloudTrackObj,
                    ctx,
                    fileStorage: ctx.gpxFiles,
                    setFileStorage: ctx.setGpxFiles,
                    recentSaver,
                }).then();
            }
        }

        if (selectedType === OBJECT_SEARCH && ctx.selectedSearchObj) {
            openSearchObj(ctx, ctx.selectedSearchObj, { recentSaver });
        }

        if (selectedType === OBJECT_SEARCH && ctx.selectedPoiObj) {
            openPoiObj(ctx, ctx.selectedPoiObj);
        }

        if (selectedType === OBJECT_TYPE_LOCAL_TRACK && ctx.selectedLocalTrackObj && !ctx.createTrack?.enable) {
            openSelectedLocalTrack(ctx);
        }
    }

    function selectMenuInfoByObjectType(force = null) {
        const currentMenu = items.find((item) => {
            return item.type === (force ?? ctx.currentObjectType);
        });
        if (currentMenu) {
            setMenuInfo(currentMenu.component);
            setSelectedType(currentMenu.type);
            ctx.setPrevPageUrl({ url: location, active: false });
        }
    }

    function isSelectedMenuItem(item) {
        return menuInfo?.type.name === item?.component?.type.name;
    }

    function setMenuStyles(item) {
        let res = [];
        //close
        !openMainMenu && res.push(styles.menuItemClose);
        //open
        openMainMenu && res.push(styles.menuItemOpen);
        //selected
        isSelectedMenuItem(item) && res.push(styles.menuItemSelected);

        return res.join(' ');
    }

    function setMenuIconStyles(item) {
        let res = [];
        //close
        !openMainMenu && res.push(styles.menuIconClose);
        //open
        openMainMenu && res.push(styles.menuItemOpen);
        //selected
        isSelectedMenuItem(item) && res.push(styles.menuItemSelected);

        return res.join(' ');
    }

    function selectMenu({ item, openFromUrl = false }) {
        doSelectMenu({ item });
    }

    function doSelectMenu({ item }) {
        const editInProgress = !!ctx.exitGuards.wptEdit?.hasChanges;
        if (!editInProgress) {
            closeSubPages({ ctx, ltx });
        }
        if (location.pathname.startsWith(item.url) && location.pathname !== item.url) {
            if (isFavoriteFromSearch(ctx) || isTrackFromSearch(ctx)) {
                navigate(item.url + liveHash());
            }
        }
        let currentType;
        if (menuInfo) {
            // update menu
            if (!editInProgress) {
                setShowInfoBlock(false);
                ctx.setCurrentObjectType(null);
            }
            ctx.setOpenNavigationSettings(false);
            ctx.setSearchSettings((prev) => ({ ...prev, showExploreMarkers: false }));
            closeCloudSettings(openCloudSettings, setOpenCloudSettings, ctx);
            const updateMenu = !isSelectedMenuItem(item) || ctx.openMenu;
            const menu = updateMenu ? item : null;
            if (!menu) {
                ctx.setInfoBlockWidth(`${MENU_INFO_CLOSE_SIZE}px`);
            }
            setMenuInfo(menu?.component);
            currentType = menu?.type;
        } else {
            // select first menu
            setMenuInfo(item.component);
            currentType = item.type;
            setOpenMainMenu(false);
            if (item.type === OBJECT_CONFIGURE_MAP) {
                ctx.setCurrentObjectType(OBJECT_CONFIGURE_MAP);
            }
        }
        ctx.setPrevPageUrl({ url: location, active: false });
        setSelectedType(currentType);

        return currentType;
    }

    useEffect(() => {
        if (ctx.openMenu) {
            const item = items.find((item) => item.id === ctx.openMenu?.id);
            selectMenu({ item });
            ctx.setOpenMenu(null);
        }
    }, [ctx.openMenu]);

    // Note: When adding new parameters, it may be necessary to refactor pageParams
    // to store a list of parameters as a map instead of a string for better flexibility.
    useEffect(() => {
        const pinPoint = mtx.pinPoint;
        if (pinPoint) {
            if (location.pathname.includes(NAVIGATE_URL) && !ctx.routeObject.isReady()) {
                return;
            }
            const pin = `${pinPoint.lat.toFixed(6)},${pinPoint.lng.toFixed(6)}`;
            const pretty = createUrlParams({ pin });
            const pageParams = { ...ctx.pageParams };
            const pinRegex = /pin=([^&]*)/;
            const newPin = pretty.match(pinRegex)[0];

            items.forEach((item) => {
                const type = item.type;
                pageParams[type] = addParamsToUrl(pageParams, type, pinRegex, newPin, pretty);
            });
            // case for main page
            if (location.pathname === MAIN_URL_WITH_SLASH) {
                pageParams[MAIN_PAGE_TYPE] = addParamsToUrl(pageParams, MAIN_PAGE_TYPE, pinRegex, newPin, pretty);
            }

            ctx.setPageParams(pageParams);
        }
    }, [mtx.pinPoint]);

    function addParamsToUrl(pageParams, type, pinRegex, newPin, pretty) {
        const existingParams = pageParams[type] || '';
        if (existingParams.match(pinRegex)) {
            return existingParams.replace(pinRegex, newPin);
        } else {
            return existingParams ? `${existingParams}&${pretty.slice(1)}` : pretty;
        }
    }

    useEffect(() => {
        const currentMenu = items.find((item) => isSelectedMenuItem(item));
        if (currentMenu && menuInfo) {
            if (currentMenu.type === OBJECT_TYPE_CLOUD_TRACK) {
                // not to navigate to the track menu if the track info is opened
                const currentUrl = globalThis.location.href;
                if (currentUrl.includes(INFO_MENU_URL)) {
                    return;
                }
            }
            setSelectedType(currentMenu?.type);
        }

        if (!menuInfo) setSelectedType(null);
    }, [menuInfo]);

    useEffect(() => {
        const menu = items.find((item) => isSelectedMenuItem(item));
        if (menu) {
            navigateToUrl({ menu, params: ctx.pageParams });
            return;
        }
        if (location.pathname === MAIN_URL_WITH_SLASH) {
            // if the menu not found, navigate to the main page
            navigateToUrl({ isMain: true, params: ctx.pageParams });
        }
    }, [ctx.pageParams]);

    useEffect(() => {
        // now this case only for login/logout
        if (ctx.prevPageUrl?.active) {
            const prev = ctx.prevPageUrl.url;
            if (prev?.pathname?.startsWith(MAIN_URL_WITH_SLASH + SEARCH_URL)) {
                navigate(prev.pathname + (prev.search || '') + liveHash());
                return;
            }
            const currentMenu = items?.find((item) => item.url === prev?.pathname);
            if (currentMenu) {
                navigateToUrl({ menu: currentMenu });
            } else {
                // if the menu not found, navigate to the main page
                navigateToUrl({ isMain: true });
            }
        }
    }, [ctx.prevPageUrl]);

    function navigateToUrl({ menu = null, isMain = false, params = null }) {
        if (menu) {
            const isSubroute = location.pathname.startsWith(menu.url) && location.pathname !== menu.url;
            if (isSubroute) {
                return;
            }
        }

        const currentUrl = location.pathname + location.search + liveHash();
        const navigateIfChanged = (targetUrl) => {
            if (targetUrl !== currentUrl) navigate(targetUrl);
        };

        if (isMain) {
            const pageType = params?.[MAIN_PAGE_TYPE] ?? '';
            navigateIfChanged(MAIN_URL_WITH_SLASH + pageType + liveHash());
        } else if (menu) {
            if (menu.type === OBJECT_TYPE_NAVIGATION_TRACK && ctx.routeObject.getOption('route.map.zoom')) {
                // auto navigation from the route (fitBounds)
                return;
            }

            const menuParams = params?.[menu.type];
            let targetUrl = menu.url;

            if (menuParams !== undefined) {
                targetUrl += menuParams;
            } else if (menu.type === OBJECT_TYPE_NAVIGATION_TRACK && !ctx.routeObject.isReady()) {
                // special case for Navigation due to lazy-loading providers
                targetUrl += location.search;
            } else if (menu.type === OBJECT_TYPE_TRAVEL && isTravelPath(location.pathname) && location.search) {
                targetUrl += location.search;
            }

            navigateIfChanged(targetUrl + liveHash());
        }
    }

    function isOpenSubMenu() {
        return showInfoBlock || openCloudSettings || aloneVisibleTracks;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'absolute',
            }}
        >
            <ClickAwayListener onClickAway={handleClickAway}>
                <Drawer
                    variant="permanent"
                    PaperProps={{
                        sx: {
                            boxSizing: 'border-box',
                            width: size,
                            mt: showInstallBanner && `${INSTALL_BANNER_SIZE}px`,
                            height: showInstallBanner ? `${height - INSTALL_BANNER_SIZE}px` : '100%',
                            overflow: 'hidden',
                            zIndex: openMainMenu ? OPEN_LEFT_MENU_Z_INDEX : LEFT_MENU_Z_INDEX,
                            borderRight:
                                ((!menuInfo &&
                                    !ltx.openLoginMenu &&
                                    ctx.infoBlockWidth === `${MENU_INFO_CLOSE_SIZE}px`) ||
                                    (menuInfo && openMainMenu)) &&
                                'none !important',
                            boxShadow:
                                !menuInfo || (menuInfo && openMainMenu)
                                    ? '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 10px 5px rgba(0,0,0,0.12);'
                                    : 'none',
                        },
                    }}
                    open={openMainMenu}
                >
                    <Toolbar />
                    <LoginButton
                        openMainMenu={openMainMenu}
                        setMenuInfo={setMenuInfo}
                        setShowInfoBlock={setShowInfoBlock}
                    />
                    <Divider sx={{ my: '0px !important' }} />
                    <div className={styles.menu}>
                        {items.map(
                            (item) =>
                                item.show && (
                                    <MenuItem
                                        id={item.id}
                                        key={item.id ?? item.type}
                                        className={setMenuStyles(item)}
                                        onClick={(event) => handleMenuClick(event, item)}
                                        onAuxClick={(event) => handleMenuAuxClick(event, item)}
                                        onMouseDown={(event) => {
                                            if (event.button === 1) {
                                                event.preventDefault();
                                            }
                                        }}
                                    >
                                        <ListItemButton
                                            className={setMenuIconStyles(item)}
                                            sx={{
                                                minWidth: '40px',
                                                maxWidth: '40px',
                                                ml: '-5px',
                                                justifyContent: openMainMenu ? 'initial' : 'center',
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    justifyContent: 'center',
                                                    ml: openMainMenu ? '-14px' : 0,
                                                }}
                                            >
                                                <SvgIcon
                                                    className={styles.customIconPath}
                                                    component={item.icon}
                                                    inheritViewBox
                                                />
                                                {menuDots[item.type] && <span className={styles.dotMenu} />}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.name}
                                                sx={{
                                                    opacity: openMainMenu ? 1 : 0,
                                                    pl: openMainMenu ? 1 : 0,
                                                    '& .MuiTypography-root': {
                                                        fontSize: '14px',
                                                    },
                                                }}
                                            />
                                        </ListItemButton>
                                    </MenuItem>
                                )
                        )}
                    </div>
                    <div className={styles.menuButton}>
                        <Divider sx={{ my: '0px !important' }} />
                        <div style={{ height: 'var(--profile-menu-button-height)' }}>
                            <MenuItem key={'Menu'}>
                                <ListItemButton
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                    onClick={handleDrawer}
                                >
                                    <div style={{ alignItems: 'center' }}>
                                        <ListItemIcon sx={{ ml: '13px' }}>
                                            <Menu />
                                        </ListItemIcon>
                                        <ListItemText
                                            sx={{
                                                ml: '7px',
                                                color: '#237bff',
                                                fontWeight: '500 !important',
                                                '& .MuiTypography-root': {
                                                    fontSize: '14px',
                                                },
                                            }}
                                        >
                                            {t('shared_string_menu')}
                                        </ListItemText>
                                    </div>
                                </ListItemButton>
                            </MenuItem>
                        </div>
                    </div>
                </Drawer>
            </ClickAwayListener>
            <Drawer
                variant="persistent"
                PaperProps={{
                    sx: {
                        width: infoSize,
                        ml: '64px',
                        mt: showInstallBanner && `${INSTALL_BANNER_SIZE}px`,
                        boxShadow: 'none',
                        zIndex: OPEN_MENU_INFOBLOCK_Z_INDEX,
                        overflow: 'hidden',
                    },
                }}
                sx={{ left: 'auto !important' }}
                open={true}
                hideBackdrop
            >
                <Toolbar sx={{ mb: '-4px' }} />
                {showDeleteOutlet && outlet}
                {showShareOutlet && (
                    // keep ShareFile mounted (it owns the join fetch); hide the list while a shared point is open
                    <div style={{ display: ctx.selectedGpxFile?.markerCurrent ? 'none' : 'block' }}>{outlet}</div>
                )}
                {aloneVisibleTracks && <VisibleTracks />}
                {!isOpenSubMenu() && (
                    <>
                        {ctx.openProFeatures ? <ProFeatures /> : isAccountOpen && outlet ? outlet : null}
                        <div
                            style={{
                                display: isAccountOpen || ctx.openProFeatures ? 'none' : 'block',
                            }}
                        >
                            {items.map((item) => {
                                const selected = selectedType === item.type;
                                if (!item.show) {
                                    return selected ? (
                                        <Empty
                                            key={item.type}
                                            title={t('web:coming_soon')}
                                            text={t('web:coming_soon_description')}
                                            checkLogin={false}
                                        />
                                    ) : null;
                                }
                                const display = selected ? 'block' : 'none';
                                const id = `se-menu-component-${item.type}-${display}`;

                                return (
                                    <div id={id} key={item.type} style={{ display }}>
                                        {item.component}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
                <InformationBlock
                    showInfoBlock={showInfoBlock}
                    setShowInfoBlock={setShowInfoBlock}
                    setClearState={setClearState}
                    mainMenuSize={size}
                    setSavePrevState={setSavePrevState}
                    trackUrlOpenLoading={trackUrlOpenLoading}
                />
                {openCloudSettings && <CloudSettings setOpenCloudSettings={setOpenCloudSettings} />}
            </Drawer>
        </Box>
    );
}
